import { Component, Input } from '@angular/core';
import { ImpactListModel } from '../../dashboard/assessment-list/impact-list.model';
import {
  AssessmentSource,
  DashboardService,
  GoToQuestionnaireAction,
  ProfileService,
  StartScoringAction,
} from '../../dashboard';
import { UserStatus } from '../../dashboard/assessment-list/user-status.enum';
import { AssessmentListModel } from '../../dashboard/assessment-list/assessment-list.model';
import { GoToImpactScanRevampAction } from '../../dashboard/xs/actions/go-to-impact-scan-revamp.action';
import { QuestionnaireType } from '../../dashboard/scoreboard/models/questionnaire.type';
import { ViewQuestionnaireAction } from '../../dashboard/xs/actions/view-questionnaire.action';
import { AssessmentVersion } from '../../dashboard/scoreboard/models/scoring.model';
import { EsgAssessmentExportService } from '../export/esg-assessment-export.service';
import { ImpactScanExportService } from '../export/impact-scan-export.service';
import { ExportDropdownMenuItem } from '../export/export-dropdown/export-dropdown-menu-item';

@Component({
  selector: 'app-assessment-group-revamp',
  templateUrl: './assessment-group.component.html',
  styleUrls: ['./assessment-group.component.scss'],
})
export class AssessmentRevampGroupComponent {
  static readonly isV2: boolean = true;

  @Input()
  impact!: ImpactListModel;

  @Input()
  expanded = false;

  constructor(
    public readonly dashboardService: DashboardService,
    public readonly profileService: ProfileService,
    private readonly esgAssessmentExportService: EsgAssessmentExportService,
    private readonly impactScanExportService: ImpactScanExportService,
  ) {}

  startAssessment(): void {
    this.dashboardService.dispatchAction(
      new StartScoringAction({
        impactScanId: parseInt(this.impact.id, 10),
        source: AssessmentSource.ASSESSMENT_LIST,
      }),
    );
  }

  goToImpact(impact: ImpactListModel): void {
    this.dashboardService.dispatchAction(
      new GoToImpactScanRevampAction({
        id: parseInt(impact.id, 10),
        status: impact.status,
      }),
    );
  }

  goToAssessment(assessment: AssessmentListModel): void {
    this.dashboardService.dispatchAction(
      new GoToQuestionnaireAction({
        id: assessment.id,
        status: assessment.status,
        type: QuestionnaireType.ASSESSMENT,
        version: assessment.assessmentVersion ?? AssessmentVersion.REVAMP,
        disabled: assessment.disabled,
        unfinished: assessment.unfinished,
        viewOnly: assessment.viewOnly,
      }),
    );
  }

  isCurrentUser(userEmail: string): boolean {
    return userEmail === this.profileService.getProfile().email;
  }

  isInactive(userStatus: UserStatus): boolean {
    return userStatus === UserStatus.INACTIVE;
  }

  viewImpact(impact: ImpactListModel): void {
    this.goToImpact(impact);
  }

  viewAssessment(assessment: AssessmentListModel): void {
    this.dashboardService.dispatchAction(
      new ViewQuestionnaireAction({
        id: assessment.id,
        status: assessment.status,
        type: QuestionnaireType.ASSESSMENT,
        version: assessment.assessmentVersion,
        userStatus: assessment.userStatus,
        firstName: assessment.firstName,
        lastName: assessment.lastName,
        disabled: assessment.disabled,
        viewOnly: assessment.viewOnly,
        unfinished: assessment.unfinished,
      }),
    );
  }

  isInProgress(scoring: AssessmentListModel): boolean {
    return scoring.unfinished && !scoring.disabled;
  }

  isCompleted(scoring: AssessmentListModel): boolean {
    return !scoring.unfinished && !scoring.disabled;
  }

  isDisabled(scoring: AssessmentListModel): boolean {
    return scoring.disabled;
  }

  canContinue(scoring: AssessmentListModel): boolean {
    return scoring.unfinished && !scoring.viewOnly;
  }

  canContinueImpact(scoring: AssessmentListModel): boolean {
    return scoring.unfinished && !scoring.viewOnly && scoring.version === 'ESG_MANAGED';
  }

  hasResults(scoring: AssessmentListModel): boolean {
    return !scoring.unfinished;
  }

  canView(scoring: AssessmentListModel): boolean {
    return scoring.viewOnly;
  }

  isEligible(): boolean {
    return !(
      this.impact.assessmentList.find((assessment) => assessment.assessmentVersion !== AssessmentVersion.MANAGED) !==
      undefined
    );
  }

  public get isV2(): boolean {
    return AssessmentRevampGroupComponent.isV2;
  }

  public exportImpactScanMenuItems(): ExportDropdownMenuItem[] {
    return [
      {
        text: 'CSV',
        id: 'csv',
        action: (): Promise<void> => this.impactScanExportService.exportImpactScanToCSV(Number(this.impact.id)),
        icon: 'table_chart',
      },
    ];
  }

  public exportEsgAssessmentMenuItems(assessment: AssessmentListModel): ExportDropdownMenuItem[] {
    return [
      {
        text: 'CSV',
        id: 'csv',
        action: (): Promise<void> => this.esgAssessmentExportService.exportAssessmentToCSV(assessment),
        icon: 'table_chart',
      },
    ];
  }
}
